import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { deltaToText } from '../../utils/text/deltaText';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem, { MagneticWallItemCard } from '../MagneticWallItem';
import { useCanShowWideMagneticWallItems } from '../PostLayout';
import CommentThreadCompact from './CommentThreadCompact';
import type { CommentThreadFullProps } from './CommentThreadFull';
import CommentThreadFull from './CommentThreadFull';
import { isClickingMagneticWallItemOverflowButton } from './MagneticWallItemOverflowMenu';

const POST_FRAGMENT = gql(`
  fragment CommentThread_post on RootPostFields {
    ...CommentThreadFull_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThread_thread on PublicThreadFields {
    id
    comments {
      id
      content
    }
    ...CommentThreadFull_thread
    ...CommentThreadCompact_thread
  }
`);

const isThreadWide = (thread: DocumentType<typeof THREAD_FRAGMENT>) => {
  const fullThreadText = thread.comments
    .map((comment) => deltaToText(comment.content))
    .join('\n');

  const lineCount = fullThreadText.match(/\n/g)?.length ?? 0;

  const charsCount = fullThreadText.length - lineCount;

  return Math.floor(charsCount / 50) > lineCount;
};

type CommentThreadProps = {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isFocused?: boolean;
  minimized?: boolean;
  isPopOverOpen?: boolean;
  initialShowingMore?: boolean;
  overlapped: boolean;
  onResize: () => void;
  size: MagneticWallItemSize;
} & Pick<
  CommentThreadFullProps,
  'unsavedInputValue' | 'onInputChange' | 'unresolveThread'
>;

const CommentThread = ({
  thread,
  post,
  isFocused = false,
  isPopOverOpen = false,
  initialShowingMore = false,
  overlapped,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
  size,
}: CommentThreadProps) => {
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const canShowWideComments = useCanShowWideMagneticWallItems();

  const isWide =
    useMemo(() => isThreadWide(thread), [thread]) && canShowWideComments;

  useEffect(() => {
    onResize?.();
  }, [onResize]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightComment(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  const handleClick = (e: React.MouseEvent) => {
    if (
      !isPopOverOpen &&
      !isClickingMagneticWallItemOverflowButton(e.nativeEvent)
    ) {
      dispatch(
        EditorActions.focusMagneticWall({
          id: thread.id,
          type: 'comment',
          isActive: true,
          hasExpandIntent: true,
        }),
      );
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
    setIsHovered(false);
  };

  const fullViewProps = {
    onResize,
    thread,
    unsavedInputValue,
    initialShowingMore,
    onInputChange,
    post,
  };

  return (
    <MagneticWallItem
      id={`thread-${thread.id}`}
      focused={isFocused}
      isPopOverOpen={isPopOverOpen}
      wide={isWide}
      popOverContent={
        <PopOverContainer noPadding width="auto" fullHeight>
          {(popoverProps: { scrollToBottom: () => void }) => (
            <CommentThreadFull
              {...fullViewProps}
              isExpanded={isFocused}
              hovered={isHovered}
              onCreated={() => {
                setTimeout(popoverProps.scrollToBottom, 200);
              }}
              unresolveThread={unresolveThread}
            />
          )}
        </PopOverContainer>
      }
      fullView={
        <MagneticWallItemCard
          focused={isFocused}
          focusable={!isFocused}
          overlapped={overlapped}
        >
          <CommentThreadFull
            {...fullViewProps}
            isExpanded={isFocused}
            hovered={isHovered}
            unresolveThread={unresolveThread}
            wide={isWide}
          />
        </MagneticWallItemCard>
      }
      onClick={handleClick}
      onBlur={handleBlur}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      compactView={
        <CommentThreadCompact isFocused={isFocused} thread={thread} />
      }
      size={size}
    />
  );
};

export default CommentThread;
